/**
 * useOnboardingSession - Manages onboarding session state, step navigation, and API calls.
 */
import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildApiUrl, tenantApiPrefix } from '../../../config';
import { getTenantIdFromStorage } from '../../../utils/onboardingLocalStorage';
import { executeRecaptcha } from '../../../utils/recaptcha';
import { setOnboardingTtvClock } from '../../../utils/onboardingLocalStorage';
import {
  coarseErrorMessage,
  identifyUserFromOnboardingVerify,
  trackOnboardingOtpResendRequested,
  trackOnboardingOtpVerifyFailed,
  trackOnboardingOtpVerifySubmitted,
  trackOnboardingSignupFailed,
  trackOnboardingSignupSubmitted,
} from '../../../utils/analytics';

export type OnboardingStep =
  | 'signup'
  | 'otp_sent'
  | 'otp_verified'
  | 'path_chosen'
  | 'sample_dataset_picked'
  | 'completed';

export type ChosenPath = 'sample' | 'own_data';
export type DatasetId = 'ecommerce' | 'marketing' | 'supply_chain';

/** Own-data flow marks the session `completed` when users go to Connectors; resume there instead of Copilot. */
export function completedSignupWizardHomePath(chosenPath: string | null | undefined): '/connectors' | '/copilot' {
  return chosenPath === 'own_data' ? '/connectors' : '/copilot';
}

export interface OnboardingSessionState {
  sessionId: string | null;
  email: string | null;
  currentStep: OnboardingStep;
  chosenPath: ChosenPath | null;
  chosenDataset: DatasetId | null;
  connectorId: string | null;
  tenantId: string | null;
}

/** Persisted session id for resume; also used by DashboardLayout signup-wizard gate. */
export const ONBOARDING_SESSION_STORAGE_KEY = 'klaris_onboarding_session_id';

const STORAGE_KEY = ONBOARDING_SESSION_STORAGE_KEY;

/**
 * FastAPI can return `detail` as a string OR as an array of validation-error
 * objects (422 Unprocessable Entity).  Always coerce to a readable string.
 */
function extractApiError(data: any, fallback: string): string {
  const detail = data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e: any) => e?.msg ?? JSON.stringify(e)).join('; ') || fallback;
  }
  return JSON.stringify(detail);
}


function getToken(): string | null {
  try { return localStorage.getItem('klaris_jwt'); } catch { return null; }
}

function authHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
}

export function useOnboardingSession() {
  const navigate = useNavigate();

  const [state, setState] = useState<OnboardingSessionState>({
    sessionId: localStorage.getItem(STORAGE_KEY),
    email: null,
    currentStep: 'signup',
    chosenPath: null,
    chosenDataset: null,
    connectorId: null,
    tenantId: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** False until we know server step when both session id + JWT exist (avoids signup flash on remount). */
  const [sessionHydrated, setSessionHydrated] = useState(() => {
    const sid = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
    const tok = typeof localStorage !== 'undefined' ? getToken() : null;
    return !(sid && tok);
  });

  // Resume session on mount when authenticated (JWT + onboarding session id)
  useEffect(() => {
    const sessionId = localStorage.getItem(STORAGE_KEY);
    const token = getToken();
    if (sessionId && token) {
      void resumeSession(sessionId).finally(() => setSessionHydrated(true));
    } else {
      setSessionHydrated(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resumeSession = useCallback(async (sessionId: string) => {
    try {
      const tid = getTenantIdFromStorage();
      if (!tid) {
        localStorage.removeItem(STORAGE_KEY);
        return;
      }
      const resp = await fetch(buildApiUrl(`${tenantApiPrefix(tid)}/onboarding/sessions/${sessionId}`), {
        headers: authHeaders(),
      });
      if (!resp.ok) {
        localStorage.removeItem(STORAGE_KEY);
        return;
      }
      const data = await resp.json();
      const step = data.current_step as OnboardingStep;
      setState({
        sessionId: data.session_id,
        email: data.email,
        currentStep: step,
        chosenPath: data.chosen_path,
        chosenDataset: data.chosen_dataset,
        connectorId: data.connector_id,
        tenantId: data.tenant_id,
      });
      if (step === 'completed') {
        navigate(completedSignupWizardHomePath(data.chosen_path), { replace: true });
      }
      // Stay on current route otherwise - avoid navigate('/login') here; it can remount
      // this route and flash the email step before state is restored.
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [navigate]);

  const signup = useCallback(async (email: string) => {
    setLoading(true);
    setError(null);
    try {
      const recaptcha_token = await executeRecaptcha('onboarding_signup');
      const payload: Record<string, string> = { email };
      if (recaptcha_token) payload.recaptcha_token = recaptcha_token;
      const resp = await fetch(buildApiUrl('/api/v1/onboarding/signup'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(extractApiError(data, 'Signup failed'));
      localStorage.setItem(STORAGE_KEY, data.session_id);
      setState((prev: OnboardingSessionState) => ({ ...prev, sessionId: data.session_id, email, currentStep: 'otp_sent' as OnboardingStep }));
      trackOnboardingSignupSubmitted({ has_session_id: Boolean(data.session_id) });
      return data;
    } catch (err: any) {
      trackOnboardingSignupFailed({ error_type: coarseErrorMessage(err) });
      setError(err instanceof Error ? err.message : String(err));
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const verifyOtp = useCallback(async (otpCode: string) => {
    if (!state.sessionId) throw new Error('No session');
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(buildApiUrl('/api/v1/onboarding/verify-otp'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: state.sessionId, otp_code: otpCode }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(extractApiError(data, 'Verification failed'));

      const nextStep = (data.current_step as OnboardingStep) || 'otp_verified';
      try {
        const u = data.user || {};
        const t = data.tenant || {};
        if (u.user_id && u.email && t.tenant_id && t.name) {
          identifyUserFromOnboardingVerify(
            {
              user_id: String(u.user_id),
              email: String(u.email),
              name: u.name ? String(u.name) : undefined,
              role_name: u.role_name ? String(u.role_name) : undefined,
            },
            { tenant_id: String(t.tenant_id), name: String(t.name) },
          );
        }
      } catch {
        /* non-critical */
      }

      // Store auth data - but do NOT dispatch auth-change here.
      // The onboarding wizard manages its own navigation via currentStep state.
      // Dispatching auth-change could trigger re-mounts that call resumeSession
      // and navigate away from the onboarding flow.
      localStorage.setItem('klaris_jwt', data.access_token);
      localStorage.setItem('klaris_user', JSON.stringify(data.user ?? {}));
      localStorage.setItem('klaris_tenant', JSON.stringify(data.tenant ?? {}));
      // Per-tenant clock for completion modal (refreshes each OTP verify / new tenant)
      const tid =
        data.tenant && typeof data.tenant === 'object' && 'tenant_id' in data.tenant
          ? (data.tenant as { tenant_id?: string }).tenant_id
          : undefined;
      setOnboardingTtvClock(tid != null ? String(tid) : undefined);
      // Wizard gate: drop session id once fully through OTP for "skip to app" tenants so
      // DashboardLayout does not race GET /sessions against navigation.
      if (nextStep === 'completed') {
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch {
          /* ignore */
        }
      } else if (data.session_id) {
        localStorage.setItem(STORAGE_KEY, data.session_id);
      }

      const tenantIdStr =
        data.tenant && typeof data.tenant === 'object' && data.tenant !== null && 'tenant_id' in data.tenant
          ? String((data.tenant as { tenant_id?: unknown }).tenant_id ?? '')
          : '';

      try {
        setState((prev: OnboardingSessionState) => ({
          ...prev,
          sessionId: data.session_id || prev.sessionId,
          email: data.user?.email ?? prev.email,
          currentStep: nextStep,
          tenantId: tenantIdStr || null,
          chosenPath: data.chosen_path ?? null,
          chosenDataset: data.chosen_dataset ?? null,
          connectorId: data.connector_id ?? null,
        }));
      } catch (stateErr) {
        console.error('[useOnboardingSession] verifyOtp setState failed', stateErr);
      }

      try {
        trackOnboardingOtpVerifySubmitted({
          current_step: nextStep,
          skips_to_copilot: nextStep === 'completed',
        });
      } catch {
        /* analytics non-fatal */
      }

      return data;
    } catch (err: any) {
      trackOnboardingOtpVerifyFailed({ error_type: coarseErrorMessage(err) });
      setError(err instanceof Error ? err.message : String(err));
      throw err;
    } finally {
      setLoading(false);
    }
  }, [state.sessionId]);

  const resendOtp = useCallback(async () => {
    if (!state.sessionId) throw new Error('No session');
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(buildApiUrl('/api/v1/onboarding/resend-otp'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: state.sessionId }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(extractApiError(data, 'Resend failed'));
      trackOnboardingOtpResendRequested({
        resend_count: typeof data.resend_count === 'number' ? data.resend_count : 0,
        max_resends: typeof data.max_resends === 'number' ? data.max_resends : undefined,
      });
      return data;
    } catch (err: any) {
      setError(err instanceof Error ? err.message : String(err));
      throw err;
    } finally {
      setLoading(false);
    }
  }, [state.sessionId]);

  const updateStep = useCallback(async (step: OnboardingStep, data: Record<string, any> = {}) => {
    if (!state.sessionId) throw new Error('No session');
    const tid = getTenantIdFromStorage();
    if (!tid) throw new Error('Tenant context missing');
    setError(null);
    try {
      const resp = await fetch(buildApiUrl(`${tenantApiPrefix(tid)}/onboarding/sessions/${state.sessionId}/step`), {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ step, data }),
      });
      const result = await resp.json();
      if (!resp.ok) throw new Error(extractApiError(result, 'Update failed'));
      setState((prev: OnboardingSessionState) => ({
        ...prev,
        currentStep: (result.current_step as OnboardingStep) ?? step,
        // API may return null - do not use || or we keep stale path after "back"
        chosenPath: (result.chosen_path ?? null) as ChosenPath | null,
        chosenDataset: (result.chosen_dataset ?? null) as DatasetId | null,
        connectorId: result.connector_id ?? null,
      }));
      return result;
    } catch (err: any) {
      setError(err instanceof Error ? err.message : String(err));
      throw err;
    }
  }, [state.sessionId]);

  const provisionSample = useCallback(async (datasetId: DatasetId) => {
    if (!state.sessionId) throw new Error('No session');
    const tid = getTenantIdFromStorage();
    if (!tid) throw new Error('Tenant context missing');
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(buildApiUrl(`${tenantApiPrefix(tid)}/onboarding/provision-sample`), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ session_id: state.sessionId, dataset_id: datasetId }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(extractApiError(data, 'Provisioning failed'));
      return data;
    } catch (err: any) {
      setError(err instanceof Error ? err.message : String(err));
      throw err;
    } finally {
      setLoading(false);
    }
  }, [state.sessionId]);

  const clearError = useCallback(() => setError(null), []);

  /** Abandon OTP step and return to email entry (wrong email, etc.). */
  const backToEmailStep = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    setError(null);
    setState({
      sessionId: null,
      email: null,
      currentStep: 'signup',
      chosenPath: null,
      chosenDataset: null,
      connectorId: null,
      tenantId: null,
    });
  }, []);

  return {
    ...state,
    loading,
    error,
    sessionHydrated,
    signup,
    verifyOtp,
    resendOtp,
    updateStep,
    provisionSample,
    clearError,
    resumeSession,
    backToEmailStep,
  };
}
